/**
 * Bridges Supabase auth tokens (stored in localStorage) with Capacitor
 * Preferences on native platforms. iOS WebView can wipe localStorage when
 * the app is backgrounded / suspended, which logs the user out. Capacitor
 * Preferences uses native key/value storage (UserDefaults on iOS, Shared
 * Preferences on Android) which survives backgrounding and app restarts.
 *
 * Strategy:
 *   1. On boot, restore any auth keys from Preferences → localStorage
 *      BEFORE the Supabase client initialises its session.
 *   2. Mirror every subsequent localStorage write/remove of auth keys
 *      back into Preferences so the two stay in sync.
 */
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

const AUTH_KEY_PATTERN = /^sb-.*-auth-token/;

const isAuthKey = (key: string | null | undefined): key is string =>
  !!key && AUTH_KEY_PATTERN.test(key);

let initialised = false;

/**
 * Restore Supabase auth tokens from native Preferences into localStorage
 * before the Supabase client reads its session. Must be awaited.
 */
export const restoreNativeAuthSession = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform() || initialised) return;
  initialised = true;

  try {
    const { keys } = await Preferences.keys();
    for (const key of keys) {
      if (!isAuthKey(key)) continue;
      const { value } = await Preferences.get({ key });
      if (value !== null && window.localStorage.getItem(key) === null) {
        window.localStorage.setItem(key, value);
      }
    }
  } catch (e) {
    console.error("[nativeAuthStorage] restore failed", e);
  }

  installLocalStorageMirror();
};

/**
 * Patch localStorage so any future writes/removes of auth keys are
 * mirrored into Capacitor Preferences. Idempotent.
 */
const installLocalStorageMirror = () => {
  if (!Capacitor.isNativePlatform()) return;
  const proto = window.Storage.prototype as Storage & {
    __nativeAuthMirrorInstalled?: boolean;
  };
  if (proto.__nativeAuthMirrorInstalled) return;
  proto.__nativeAuthMirrorInstalled = true;

  const originalSetItem = proto.setItem;
  const originalRemoveItem = proto.removeItem;

  proto.setItem = function (key: string, value: string) {
    originalSetItem.call(this, key, value);
    if (this === window.localStorage && isAuthKey(key)) {
      Preferences.set({ key, value }).catch((e) =>
        console.error("[nativeAuthStorage] mirror set failed", e)
      );
    }
  };

  proto.removeItem = function (key: string) {
    originalRemoveItem.call(this, key);
    if (this === window.localStorage && isAuthKey(key)) {
      Preferences.remove({ key }).catch((e) =>
        console.error("[nativeAuthStorage] mirror remove failed", e)
      );
    }
  };
};
